import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Dialog } from '@headlessui/react';
import { MapPin, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { shippingService } from '../services/shipping.service';
import {
  applyRecommendedToClientAddress,
  formatSendcloudInvalidMessage,
  formatSendcloudStreet,
  isSendcloudAddressValid,
  pickRecommendedSendcloudAddress,
  recommendedChangesClientForm,
  sendcloudInvalidClientFields,
  validateClientAddress,
  type SendcloudRawAddress,
} from '../services/address-validate.service';
import DeliveryAddressForm, {
  mapGroupAddressToForm,
  toDeliveryAddressPayload,
  type DeliveryAddressForm as DeliveryAddressFormData,
  type DeliveryAddressFormHandle,
  type GroupDeliveryAddress,
} from './DeliveryAddressForm';

type CheckStatus = 'idle' | 'checking' | 'verified' | 'invalid' | 'error' | 'suggest';

interface ChangeDeliveryAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupOrderId: string;
  initialAddress?: GroupDeliveryAddress | Record<string, unknown> | null;
  onSuccess?: (address: Record<string, unknown>) => void;
}

export default function ChangeDeliveryAddressModal({
  isOpen,
  onClose,
  groupOrderId,
  initialAddress,
  onSuccess,
}: ChangeDeliveryAddressModalProps) {
  const formRef = useRef<DeliveryAddressFormHandle>(null);
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddressFormData>(() =>
    mapGroupAddressToForm(initialAddress as GroupDeliveryAddress | undefined)
  );
  const [saving, setSaving] = useState(false);
  const [checkStatus, setCheckStatus] = useState<CheckStatus>('idle');
  const [recommended, setRecommended] = useState<SendcloudRawAddress | null>(null);
  const [invalidMessage, setInvalidMessage] = useState('');
  const [invalidFields, setInvalidFields] = useState<Array<keyof DeliveryAddressFormData>>([]);
  // Track open edge so we hydrate before paint (avoids one empty frame + effect races).
  const [wasOpen, setWasOpen] = useState(false);

  if (isOpen && !wasOpen) {
    setWasOpen(true);
    setDeliveryAddress(mapGroupAddressToForm(initialAddress as GroupDeliveryAddress | undefined));
    setCheckStatus('idle');
    setRecommended(null);
    setInvalidMessage('');
    setInvalidFields([]);
    setSaving(false);
  } else if (!isOpen && wasOpen) {
    setWasOpen(false);
  }

  useEffect(() => {
    if (!isOpen) return;
    setDeliveryAddress(mapGroupAddressToForm(initialAddress as GroupDeliveryAddress | undefined));
  }, [isOpen, initialAddress]);

  const persistAddress = async (addressToSave: DeliveryAddressFormData) => {
    if (!groupOrderId) {
      toast.error('No se encontró el pedido');
      return;
    }

    setSaving(true);
    const toastId = toast.loading('Guardando dirección...');

    try {
      const payload = toDeliveryAddressPayload(addressToSave, 'group_order');
      const result = await shippingService.updateGroupOrderDeliveryAddress(groupOrderId, payload);

      if (!result.success) {
        toast.error(result.error || 'No se pudo guardar la dirección', { id: toastId });
        return;
      }

      const savedAddress = result.address ?? {
        recipient_name: payload.recipient_name,
        recipient_email: payload.recipient_email,
        recipient_phone: payload.recipient_phone,
        recipient_line_1: payload.recipient_line_1,
        recipient_line_2: payload.recipient_line_2,
        recipient_city: payload.recipient_city,
        recipient_postal_code: payload.recipient_postal_code,
        recipient_province: payload.recipient_province,
        recipient_country: payload.recipient_country,
        owner_type: 'group_order',
      };

      toast.success('Dirección actualizada', { id: toastId });
      onSuccess?.(savedAddress);
      onClose();
    } catch {
      toast.error('No se pudo guardar la dirección', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formRef.current?.validate() || saving || checkStatus === 'checking') return;

    const alreadySawSuggest = checkStatus === 'suggest';
    setCheckStatus('checking');
    setInvalidMessage('');
    setInvalidFields([]);

    try {
      const result = await validateClientAddress(deliveryAddress);
      const suggestion = pickRecommendedSendcloudAddress(result);

      if (suggestion && recommendedChangesClientForm(deliveryAddress, suggestion) && !alreadySawSuggest) {
        setRecommended(suggestion);
        setCheckStatus('suggest');
        return;
      }

      if (!isSendcloudAddressValid(result)) {
        setRecommended(null);
        setInvalidMessage(formatSendcloudInvalidMessage(result));
        setInvalidFields(sendcloudInvalidClientFields(result));
        setCheckStatus('invalid');
        return;
      }

      setRecommended(null);
      setCheckStatus('verified');
      await persistAddress(deliveryAddress);
    } catch {
      setCheckStatus('error');
      toast.error('No se pudo comprobar la dirección');
    }
  };

  const handleUseRecommended = () => {
    if (!recommended) return;
    setDeliveryAddress(applyRecommendedToClientAddress(deliveryAddress, recommended));
    setRecommended(null);
    setCheckStatus('idle');
    setInvalidFields([]);
    setInvalidMessage('');
  };

  const busy = saving || checkStatus === 'checking';

  return (
    <Dialog open={isOpen} onClose={busy ? () => undefined : onClose} className="relative z-[99999]">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-[90vw] max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
          <div className="flex-shrink-0 px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
            <Dialog.Title className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <MapPin className="h-4 w-4 text-gray-400" />
              Cambiar dirección de entrega
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="bg-red-50 rounded-lg p-2 hover:bg-red-100 transition-colors duration-300 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-red-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <DeliveryAddressForm
                ref={formRef}
                deliveryAddress={deliveryAddress}
                setDeliveryAddress={setDeliveryAddress}
                highlightFields={invalidFields}
                onClearHighlight={(field) =>
                  setInvalidFields((prev) => prev.filter((item) => item !== field))
                }
                restrictToSpainIndividual
              />

              {checkStatus === 'checking' && (
                <p className="text-sm text-gray-600">Comprobando la dirección…</p>
              )}
              {checkStatus === 'verified' && (
                <p className="text-sm text-green-700">Dirección verificada</p>
              )}
              {checkStatus === 'error' && (
                <p className="text-sm text-red-600">No se pudo comprobar la dirección</p>
              )}
              {checkStatus === 'invalid' && (
                <p className="text-sm text-amber-900">{invalidMessage}</p>
              )}
              {checkStatus === 'suggest' && recommended && (
                <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-medium text-amber-900">Dirección sugerida</p>
                  <p className="text-sm text-gray-700">{formatSendcloudStreet(recommended)}</p>
                  <button
                    type="button"
                    onClick={handleUseRecommended}
                    disabled={busy}
                    className="px-3 py-1.5 rounded-lg border border-amber-300 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                  >
                    Usar esta dirección
                  </button>
                  <p className="text-xs text-gray-500">
                    O pulsa Guardar de nuevo para mantener la tuya si es válida.
                  </p>
                </div>
              )}
            </div>

            <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="px-3 py-1.5 rounded-lg text-sm border border-gray-200 hover:bg-gray-50 text-gray-700 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={busy}
                className="px-3 py-1.5 rounded-lg text-sm bg-red-50 hover:bg-red-100 text-red-500 font-semibold disabled:opacity-50"
              >
                {checkStatus === 'checking'
                  ? 'Comprobando…'
                  : saving
                    ? 'Guardando...'
                    : 'Guardar'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
